import { useMemo } from 'react';

import { useBreakpoint } from '@hivebrite/design-system/Theme';

import PageHeader from 'hb-react/frontoffice/apps/Mentoring/components/PageHeader';
import PageHeaderLayout from 'hb-react/frontoffice/apps/Mentoring/components/PageHeader/Layout';
import CardLoader from 'hb-react/frontoffice/apps/Mentoring/components/SharedCard/Loader';
import { useGetMentorProfileFromUser } from 'hb-react/frontoffice/apps/Mentoring/hooks/useGetMentorProfileFromUser';
import { useGetMentorsCustomAttributes } from 'hb-react/frontoffice/apps/Mentoring/hooks/useGetMentorsCustomAttributes';
import type {
  CustomAttributeType,
  Program,
} from 'hb-react/frontoffice/apps/Mentoring/types';
import {
  getLowerCaseMentorLabel,
  getProgramTopicId,
} from 'hb-react/frontoffice/apps/Mentoring/utils';
import { makeFilters } from 'hb-react/frontoffice/apps/Mentoring/utils/filtersUtils';
import {
  ITEMS_BY_ROW,
  makeFetchItems,
  makeItemRenderer,
} from 'hb-react/frontoffice/apps/Mentoring/utils/rendererUtils';
import BlocksRenderer from 'hb-react/frontoffice/components/BlocksRenderer';
import { ITEMS_QUERY_KEYS } from 'hb-react/frontoffice/components/BlocksRenderer/blocks/Items/utils/queryUtils';
import { BLOCKS } from 'hb-react/frontoffice/components/BlocksRenderer/shared';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import type { User } from 'hb-react/types';

interface MentorsRendererProps {
  program: Program;
  attributes: CustomAttributeType[];
  currentUser: User;
  disabledAccess?: boolean;
}

const MentorsRenderer = ({
  program,
  attributes,
  currentUser,
  disabledAccess,
}: MentorsRendererProps) => {
  const { currentNetwork } = useDefaultAppContexts();
  const breakpoint = useBreakpoint();
  const {
    id: programId,
    mentor_label: mentorLabel,
    config,
    parent_id: parentId,
    parent_type: parentType,
  } = program;
  const { data: mentorProfile } = useGetMentorProfileFromUser({
    program,
    userId: currentUser.id,
  });

  const { data } = useGetMentorsCustomAttributes({
    program,
    queryConfig: {
      is_displayed_in_mentoring_card: true,
    },
  });

  const mentorCustomizableAttributes = data?.pages?.[0]?.data || [];

  const mentoringLabel =
    currentNetwork?.features?.mentoring?.config?.feature_name;

  const filters = useMemo(
    () => makeFilters({ mentorLabel, config, attributes }),
    [mentorLabel, config, attributes],
  );
  const fetchItems = makeFetchItems(program);
  const itemRenderer = makeItemRenderer({
    program,
    disabledAccess,
    mentorCustomizableAttributes,
  });

  const mentorsBlockId = ITEMS_QUERY_KEYS.mentoring.items(programId);

  return (
    <>
      <PageHeaderLayout>
        <PageHeader
          program={program}
          status={mentorProfile?.status}
          disabledAccess={disabledAccess}
        />
      </PageHeaderLayout>
      <BlocksRenderer
        templateProps={{
          topicId: getProgramTopicId({ parentId, parentType }),
          hasFilters: filters?.length > 0,
        }}
        blocks={[
          filters?.length > 0 && {
            ...BLOCKS.filters,
            attributes: {
              filters,
            },
          },
          {
            id: mentorsBlockId,
            component: BLOCKS.items.component,
            attributes: {
              fetchItems,
              itemLoader: CardLoader,
              itemRenderer,
              itemsByRow: ITEMS_BY_ROW[breakpoint],
              loaderLines: 1,
              withCursorPagination: true,
              shouldHideIfEmpty: true,
            },
          },
          {
            id: BLOCKS.emptyContent.id,
            component: BLOCKS.emptyContent.component,
            attributes: {
              shouldDisplay: ({ data }) =>
                data?.[mentorsBlockId]?.length === 0 &&
                !data?.[BLOCKS.filters.id],
              title: s__(
                'Mentoring|There are no active %{mentorLabel} in this %{programLabel} yet',
              ).i__({
                programLabel: mentoringLabel || 'program',
                mentorLabel: getLowerCaseMentorLabel(mentorLabel),
              }),
              description: '',
              faIconProps: {
                iconName: 'handshake',
              },
            },
          },
        ].filter(Boolean)}
      />
    </>
  );
};

export default MentorsRenderer;
